/**
 * Reusable PhotoSwipe lightbox for any page region (blog prose, landing sections, etc.).
 *
 * Usage:
 *   <photoswipe-gallery max-zoom="3" class="prose">…images…</photoswipe-gallery>
 *   <div data-photoswipe-gallery data-photoswipe-max-zoom="3">…</div>
 *
 * Opt out per image: data-no-photoswipe
 * Images inside <product-gallery> are ignored (product page has its own lightbox).
 *
 * Preview source priority (mobile viewport max-width 720px):
 *   1. data-pswp-mobile-src / data-pswp-mobile-srcset (+ optional mobile width/height)
 *   2. data-pswp-src / data-pswp-srcset (PC fallback on mobile if mobile attrs missing)
 *   3. img srcset (largest candidate)
 *   4. image.currentSrc || image.src
 *
 * Desktop uses data-pswp-src / data-pswp-srcset, then steps 3–4.
 *
 * Without data-pswp-*-width/height for the active viewport, slide height uses the viewport
 * (preview opens with height filling the screen; width follows image aspect ratio).
 */
import { PhotoSwipeLightbox } from "vendor";

const MOBILE_VIEWPORT_MQ = "(max-width: 720px)";

const CLOSE_SVG = `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
  <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
  <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
  <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
</svg>`;

function getThemeStrings() {
  return window.themeVariables?.strings ?? {};
}

function registerBottomBar(photoswipe) {
  photoswipe.on("uiRegister", () => {
    photoswipe.pswp.ui.registerElement({
      name: "bottom-bar",
      order: 5,
      appendTo: "wrapper",
      html: `
        <div class="pagination">
          <button type="button" class="pagination__item group" rel="prev">
            <span class="animated-arrow animated-arrow--reverse"></span>
          </button>
          <span class="pagination__current text-sm">
            <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
          </span>
          <button type="button" class="pagination__item group" rel="next">
            <span class="animated-arrow"></span>
          </button>
        </div>
      `,
      onInit: (el, pswp) => {
        el.querySelector(".pagination__page-count").innerText = pswp.getNumItems();
        el.querySelector('[rel="prev"]')?.addEventListener("click", () => pswp.prev());
        el.querySelector('[rel="next"]')?.addEventListener("click", () => pswp.next());
        pswp.on("change", () => {
          el.querySelector(".pagination__current-page").innerText = pswp.currIndex + 1;
        });
      }
    });
  });
}

function createLightbox(maxZoomLevel = 3) {
  const strings = getThemeStrings();
  const photoswipe = new PhotoSwipeLightbox({
    pswpModule: () => import("photoswipe"),
    bgOpacity: 1,
    maxZoomLevel,
    closeTitle: strings.closeGallery ?? "Close",
    zoomTitle: strings.zoomGallery ?? "Zoom",
    errorMsg: strings.errorGallery ?? "Image not loaded",
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    zoom: false,
    closeSVG: CLOSE_SVG
  });

  photoswipe.addFilter("thumbEl", (thumbEl, data) => data["thumbnailElement"]);
  registerBottomBar(photoswipe);
  photoswipe.init();
  return photoswipe;
}

/**
 * @param {string} srcset
 * @returns {{ url: string, width: number } | null}
 */
function parseSrcsetLargest(srcset) {
  if (!srcset?.trim()) {
    return null;
  }

  const candidates = srcset
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      const descriptorMatch = trimmed.match(/^(\S+)\s+(\d+(?:\.\d+)?)(w|x)$/);

      if (descriptorMatch) {
        const url = descriptorMatch[1];
        const value = parseFloat(descriptorMatch[2]);
        const unit = descriptorMatch[3];

        return {
          url,
          width: unit === "w" ? value : value * 1000
        };
      }

      const url = trimmed.split(/\s+/)[0];
      return url ? { url, width: 0 } : null;
    })
    .filter(Boolean);

  if (!candidates.length) {
    return null;
  }

  return candidates.reduce((best, candidate) => (candidate.width > best.width ? candidate : best));
}

/**
 * @param {HTMLImageElement} image
 * @returns {{ width: number, height: number }}
 */
function getDisplayedImageDimensions(image) {
  return {
    width: parseInt(image.getAttribute("width"), 10) || image.naturalWidth || image.width || 0,
    height: parseInt(image.getAttribute("height"), 10) || image.naturalHeight || image.height || 0
  };
}

const imageNaturalDimensionsCache = new Map();

/**
 * @param {string} src
 * @returns {Promise<{ width: number, height: number } | null>}
 */
function loadImageNaturalDimensions(src) {
  if (!src) {
    return Promise.resolve(null);
  }

  if (!imageNaturalDimensionsCache.has(src)) {
    imageNaturalDimensionsCache.set(
      src,
      new Promise((resolve) => {
        const previewImage = new window.Image();
        let settled = false;

        const finish = (dimensions) => {
          if (!settled) {
            settled = true;
            resolve(dimensions);
          }
        };

        previewImage.onload = () => {
          const width = previewImage.naturalWidth || previewImage.width || 0;
          const height = previewImage.naturalHeight || previewImage.height || 0;

          finish(width > 0 && height > 0 ? { width, height } : null);
        };
        previewImage.onerror = () => finish(null);
        previewImage.src = src;

        if (previewImage.complete) {
          previewImage.onload();
        }
      })
    );
  }

  return imageNaturalDimensionsCache.get(src);
}

/**
 * @param {{ width: number, height: number } | null} sourceDimensions
 * @returns {{ width: number, height: number }}
 */
function resolveViewportHeightDimensions(sourceDimensions) {
  const viewportHeight = Math.max(1, window.innerHeight || document.documentElement?.clientHeight || 1);
  const aspectWidth = sourceDimensions?.width > 0 ? sourceDimensions.width : 16;
  const aspectHeight = sourceDimensions?.height > 0 ? sourceDimensions.height : 9;

  return {
    width: Math.max(1, Math.round(viewportHeight * (aspectWidth / aspectHeight))),
    height: viewportHeight
  };
}

/**
 * @param {HTMLImageElement} image
 * @param {string} src
 * @param {string | null} explicitWidth
 * @param {string | null} explicitHeight
 * @returns {Promise<{ width: number, height: number }>}
 */
async function resolveSlideDimensions(image, src, explicitWidth, explicitHeight) {
  const parsedExplicitWidth = parseInt(explicitWidth, 10);
  const parsedExplicitHeight = parseInt(explicitHeight, 10);

  if (parsedExplicitWidth > 0 && parsedExplicitHeight > 0) {
    return { width: parsedExplicitWidth, height: parsedExplicitHeight };
  }

  const naturalDimensions = await loadImageNaturalDimensions(src);
  return resolveViewportHeightDimensions(naturalDimensions || getDisplayedImageDimensions(image));
}

function isMobileViewport() {
  return window.matchMedia(MOBILE_VIEWPORT_MQ).matches;
}

/**
 * Resolves PC / mobile explicit PhotoSwipe attrs for the current viewport.
 * @param {HTMLImageElement} image
 * @returns {{ src?: string, srcset?: string, width: string | null, height: string | null } | null}
 */
function getPswpExplicitAttrs(image) {
  const mobileSrc = image.getAttribute("data-pswp-mobile-src");
  const mobileSrcset = image.getAttribute("data-pswp-mobile-srcset");
  const mobileWidth = image.getAttribute("data-pswp-mobile-width");
  const mobileHeight = image.getAttribute("data-pswp-mobile-height");

  const pcSrc = image.getAttribute("data-pswp-src");
  const pcSrcset = image.getAttribute("data-pswp-srcset");
  const pcWidth = image.getAttribute("data-pswp-width");
  const pcHeight = image.getAttribute("data-pswp-height");

  if (isMobileViewport()) {
    if (mobileSrc) {
      return {
        src: mobileSrc,
        srcset: mobileSrcset || undefined,
        width: mobileWidth,
        height: mobileHeight
      };
    }

    if (mobileSrcset) {
      return {
        srcset: mobileSrcset,
        width: mobileWidth,
        height: mobileHeight
      };
    }
  }

  if (pcSrc) {
    return {
      src: pcSrc,
      srcset: pcSrcset || undefined,
      width: pcWidth,
      height: pcHeight
    };
  }

  if (pcSrcset) {
    return {
      srcset: pcSrcset,
      width: pcWidth,
      height: pcHeight
    };
  }

  return null;
}

async function imageToSlideData(image) {
  const thumbnailSrc = image.currentSrc || image.src;
  const explicit = getPswpExplicitAttrs(image);

  let src = thumbnailSrc;
  let srcset;
  let dimensions;

  if (explicit?.src) {
    src = explicit.src;
    srcset = explicit.srcset;
    dimensions = await resolveSlideDimensions(image, src, explicit.width, explicit.height);
  } else if (explicit?.srcset) {
    const largest = parseSrcsetLargest(explicit.srcset);

    if (largest) {
      src = largest.url;
      dimensions = await resolveSlideDimensions(image, src, explicit.width, explicit.height);
    } else {
      dimensions = await resolveSlideDimensions(image, src, explicit.width, explicit.height);
    }
  } else {
    const largestFromImage = parseSrcsetLargest(image.getAttribute("srcset") || image.srcset);

    if (largestFromImage?.url) {
      src = largestFromImage.url;
      dimensions = await resolveSlideDimensions(image, src, null, null);
    } else {
      dimensions = await resolveSlideDimensions(image, src, null, null);
    }
  }

  return {
    thumbnailElement: image,
    src,
    srcset,
    msrc: thumbnailSrc,
    width: dimensions.width,
    height: dimensions.height,
    alt: image.alt || "",
    thumbCropped: true
  };
}

function shouldSkipImage(image) {
  if (!image?.src) {
    return true;
  }
  if (image.hasAttribute("data-no-photoswipe")) {
    return true;
  }
  if (image.closest("product-gallery")) {
    return true;
  }

  const width = parseInt(image.getAttribute("width"), 10) || image.width || 0;
  const height = parseInt(image.getAttribute("height"), 10) || image.height || 0;
  if (width > 0 && width < 48 && height > 0 && height < 48) {
    return true;
  }

  return false;
}

class PhotoswipeGalleryController {
  constructor(root, { maxZoom = 3 } = {}) {
    this.root = root;
    this.maxZoom = maxZoom;
    this._lightbox = null;
    this._onClick = this._onClick.bind(this);
    root.addEventListener("click", this._onClick);
    root.classList.add("photoswipe-gallery--enabled");
  }

  destroy() {
    this.root.removeEventListener("click", this._onClick);
    this.root.classList.remove("photoswipe-gallery--enabled");
    this._lightbox = null;
  }

  getImages() {
    return Array.from(this.root.querySelectorAll("img")).filter((image) => !shouldSkipImage(image));
  }

  async open(index = 0) {
    const images = this.getImages();
    if (!images.length) {
      return;
    }

    if (!this._lightbox) {
      this._lightbox = createLightbox(this.maxZoom);
    }

    this._lightbox.loadAndOpen(index, await Promise.all(images.map(imageToSlideData)));
  }

  _onClick(event) {
    const image = event.target.closest("img");
    if (!image || !this.root.contains(image) || shouldSkipImage(image)) {
      return;
    }

    const link = image.closest("a[href]");
    if (link) {
      event.preventDefault();
      event.stopPropagation();
    }

    const images = this.getImages();
    const index = images.indexOf(image);
    if (index < 0) {
      return;
    }

    this.open(index);
  }
}

class PhotoswipeGallery extends HTMLElement {
  connectedCallback() {
    const maxZoom = parseInt(this.getAttribute("max-zoom"), 10) || 3;
    this._controller = new PhotoswipeGalleryController(this, { maxZoom });
  }

  disconnectedCallback() {
    this._controller?.destroy();
    this._controller = null;
  }
}

function initDataAttributeGalleries(root = document) {
  root.querySelectorAll("[data-photoswipe-gallery]").forEach((element) => {
    if (element.tagName.toLowerCase() === "photoswipe-gallery" || element._photoswipeGalleryController) {
      return;
    }

    const maxZoom = parseInt(element.getAttribute("data-photoswipe-max-zoom"), 10) || 3;
    element._photoswipeGalleryController = new PhotoswipeGalleryController(element, { maxZoom });
  });
}

if (!window.customElements.get("photoswipe-gallery")) {
  window.customElements.define("photoswipe-gallery", PhotoswipeGallery);
}

initDataAttributeGalleries();

document.addEventListener("shopify:section:load", (event) => {
  initDataAttributeGalleries(event.target);
});

export {
  PhotoswipeGalleryController,
  createLightbox,
  imageToSlideData,
  parseSrcsetLargest,
  isMobileViewport,
  getPswpExplicitAttrs
};
